import { FormattedMessage, useIntl } from 'react-intl';
import {
    Button,
    CheckboxGroup,
    Input,
    Label,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalLayout,
    ModalSubtitle,
    ModalTitle,
    RadioGroup,
} from '@tlx/atlas';

import { useState } from 'react';
import { SalarySettingsDTO, updateSalarySettings } from '@Page/Onboarding/hooks/useSalarySettings';
import { SALARY_PAY_IN_ADVANCE } from '@Page/SalaryRun/hooks/useSuggestChangingPayInAdvance.ts';
import useLocalStorage from '@Hooks/storage.ts';
import { createOrdinalFromArray } from '@Util/misc.ts';

type Props = {
    handleClose: () => void;
    suggestChangePayInAdvance: number;
};

export function PayInAdvanceModal({ handleClose, suggestChangePayInAdvance }: Props) {
    const intl = useIntl();
    const [isChangeSelected, setIsChangeSelected] = useState<boolean>(true);
    const [, setHidePayInAdvance] = useLocalStorage('hide_pay_in_advance', false);

    const handleOnSubmit = async () => {
        if (isChangeSelected) {
            const dto: Partial<SalarySettingsDTO> = {
                isPaidInAdvance: suggestChangePayInAdvance === SALARY_PAY_IN_ADVANCE.SUGGEST_TURN_ON,
            };
            updateSalarySettings(dto);
        } else {
            setHidePayInAdvance(true);
        }
        handleClose();
    };

    return (
        <Modal
            open={true}
            onClose={() => {
                handleClose();
            }}
            className={'tsk-width-500'}
        >
            <ModalLayout>
                <ModalCloseButton />
                <ModalTitle>
                    <FormattedMessage id={'tsk_text_salary_advance_payment_title'} />
                </ModalTitle>
                <ModalSubtitle>
                    <FormattedMessage
                        id={'tsk_text_salary_advance_payment_suggestion'}
                        values={createOrdinalFromArray([
                            intl
                                .formatMessage({
                                    id:
                                        suggestChangePayInAdvance === SALARY_PAY_IN_ADVANCE.SUGGEST_TURN_ON
                                            ? 'tsk_text_previous'
                                            : 'tsk_text_current',
                                })
                                .toLowerCase(),
                        ])}
                    />
                </ModalSubtitle>
                <ModalContent>
                    <RadioGroup className="atl-flex atl-flex-col">
                        <CheckboxGroup>
                            <Input
                                type="radio"
                                name="change-radio"
                                id="change-radio-do-change"
                                data-testid="change-radio-do-change"
                                checked={isChangeSelected}
                                onChange={() => {
                                    setIsChangeSelected(true);
                                }}
                            />
                            <Label htmlFor="change-radio-do-change">
                                <FormattedMessage
                                    id={'tsk_text_salary_advance_payment_affirmative'}
                                    values={createOrdinalFromArray([
                                        intl
                                            .formatMessage({
                                                id:
                                                    suggestChangePayInAdvance === SALARY_PAY_IN_ADVANCE.SUGGEST_TURN_ON
                                                        ? 'tsk_text_previous'
                                                        : 'tsk_text_current',
                                            })
                                            .toLowerCase(),
                                    ])}
                                />
                            </Label>
                        </CheckboxGroup>
                        <RadioGroup>
                            <Input
                                type="radio"
                                name="change-radio"
                                id="change-radio-do-not-change"
                                data-testid="asset-import-radio-start-option-1"
                                checked={!isChangeSelected}
                                onChange={() => {
                                    setIsChangeSelected(false);
                                }}
                            />
                            <Label htmlFor="change-radio-do-not-change">
                                <FormattedMessage
                                    id={'tsk_text_salary_advance_payment_negative'}
                                    values={createOrdinalFromArray([
                                        intl
                                            .formatMessage({
                                                id:
                                                    suggestChangePayInAdvance === SALARY_PAY_IN_ADVANCE.SUGGEST_TURN_ON
                                                        ? 'tsk_text_current'
                                                        : 'tsk_text_previous',
                                            })
                                            .toLowerCase(),
                                    ])}
                                />
                            </Label>
                        </RadioGroup>
                    </RadioGroup>
                </ModalContent>
                <ModalFooter className={'atl-flex atl-gap-8'}>
                    <Button variant={'primary'} data-testid="pay-in-advance-ok-button" onClick={handleOnSubmit}>
                        <FormattedMessage id={'text_ok'} />
                    </Button>

                    <Button
                        variant={'secondary'}
                        onClick={() => {
                            handleClose();
                        }}
                        data-testid="pay-in-advance-modal-cancel-button"
                    >
                        <FormattedMessage id={'text_skip'} />
                    </Button>
                </ModalFooter>
            </ModalLayout>
        </Modal>
    );
}
